import { useState, useCallback } from 'react';
import { accountsService } from '@/services/api/api';
import { useAuthStore } from '@/stores/authStore';

function useAccountCreation() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [formData, setFormData] = useState({
    organization: '',
    organizationalUnit: '',
    name: '',
    email: '',
    Opco: '',
    Type: '',
    Domain: '',
    description: '',
    itDemand: '',
    budgetLineReference: '',
    costCenter: '',
    monthlyBudget:'',
    group: '',
    accountOwner: null,
    accountable: null,
    billing: null,
    operations: null
  });

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      organization: '',
      organizationalUnit: '',
      name: '',
      email: '',
      Opco: '',
      Type: '',
      Domain: '',
      description: '',
      itDemand: '',
      budgetLineReference: '',
      costCenter: '',
      monthlyBudget:'',
      group: '',
      accountOwner: null,
      accountable: null,
      billing: null,
      operations: null
    });
    setResetKey(prev => prev + 1);
    setMessage('');
  }, []);

  const validateForm = useCallback(() => {
    const requiredFields = ['organization', 'organizationalUnit', 'name', 'email', 'Opco', 'Type', 'Domain', 'accountOwner', 'accountable'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setMessage(`Error: Missing required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    return true;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setLoading(true);
    setMessage('');

    const { currentUser } = useAuthStore.getState();
    const userEmail = currentUser?.email || currentUser?.username || '';

    try {
      const response = await accountsService.createAccount({
        account_name: formData.name,
        account_email: formData.email,
        organizational_unit: `${formData.organizationalUnit.OrganizationalUnitName} (${formData.organizationalUnit.OrganizationalUnitId})`, 
        sso_email: userEmail, 
        sso_first_name: "User", 
        sso_last_name: "Name", 
        aws_organization: formData.organization,
        aws_opco: formData.Opco,
        aws_type: formData.Type,
        aws_domain: formData.Domain,
        account_description: formData.description,
        account_itdemand: formData.itDemand,
        budget_line_reference: formData.budgetLineReference,
        cost_center: formData.costCenter,
        monthlyBudget:formData.monthlyBudget,
        group: formData.group,
        account_owner: formData.accountOwner,
        accountable: formData.accountable,
        billing: formData.billing,
        operations: formData.operations
      });
      
      resetForm();
      
      if (response.status === 200 && response.data?.record_id) {
        setMessage(`Account creation request submitted successfully! Tracking ID: ${response.data.record_id}`);
      } else {
        setMessage('Error: Account creation request failed - No tracking ID received');
      }
    } catch (error) {
      let errorMessage = 'Error to create account: ';
      if (error.response) {
        const serverMessage = error.response.data?.message 
          || error.response.data?.error 
          || error.response.statusText;
        errorMessage += `${error.response.status} - ${serverMessage}`;
      } else if (error.request) {
        errorMessage += 'Could not connect to the server. Please verify the API URL.';
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, resetForm]);

  return {
    formData,
    loading,
    message,
    resetKey,
    updateFormData,
    resetForm,
    handleSubmit,
    setMessage
  };
}

export default useAccountCreation; 